import React from 'react'
import { FormLabel } from '@/components/ui/forms/FormLabel'
import { Option, FormGapSize } from '@/types/form'
import { GAP_CLASSES } from '@/utilities/formStyles'
import { cn } from '@/utilities/ui'

type FormRadioOption = Option & {
  description?: string
}

interface FormRadioGroupProps {
  name: string
  label?: string
  value: string
  onChange: (value: string) => void
  options: FormRadioOption[]
  required?: boolean
  showInfoIcon?: boolean
  error?: string
  className?: string
  labelClassName?: string
  groupClassName?: string
  optionClassName?: string
  radioClassName?: string
  gap?: FormGapSize
  // New props for consistency with other form components
  errorText?: string
  helperText?: string
  disabled?: boolean
  fullWidth?: boolean
  onBlur?: () => void
  testIdPrefix?: string
  showError?: boolean
  /** Mask the chosen option label. Other option labels and the question label stay unmasked. */
  maskSelectedValue?: boolean
}

export function FormRadioGroup({
  name,
  label,
  value,
  onChange,
  options,
  required = false,
  showInfoIcon = false,
  error,
  className = '',
  labelClassName = '',
  groupClassName = '',
  optionClassName = '',
  radioClassName = '',
  gap = 'lg',
  // New props
  errorText,
  helperText,
  disabled = false,
  fullWidth = false,
  onBlur,
  testIdPrefix,
  showError = true,
  maskSelectedValue = false,
}: Readonly<FormRadioGroupProps>) {
  const hasError = Boolean(error || errorText)
  const shouldShowError = showError && hasError
  const groupLabelFor = options?.[0] ? `${name}-${options[0].value}` : `${name}-option`

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <FormLabel
          id={`${name}-label`}
          htmlFor={groupLabelFor}
          className={cn('flex items-center mb-2 text-sm md:text-base md:mb-4', labelClassName)}
          required={required}
          showInfoIcon={showInfoIcon}
        >
          {label}
        </FormLabel>
      )}
      <div
        className={cn(
          'flex',
          GAP_CLASSES[gap],
          maskSelectedValue && 'ph-no-capture notranslate',
          groupClassName,
        )}
        role="radiogroup"
        aria-labelledby={label ? `${name}-label` : undefined}
      >
        {options.map((option) => {
          const optionId = `${name}-${option.value}`
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={cn(
                'flex items-center gap-3 cursor-pointer',
                disabled && 'opacity-50 cursor-not-allowed',
                optionClassName,
              )}
              data-testid={`${name}-${option.value}`}
            >
              <input
                type="radio"
                id={optionId}
                name={name}
                value={option.value}
                data-testid={testIdPrefix ? `${testIdPrefix}-${option.value}` : undefined}
                checked={value === option.value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                disabled={disabled}
                className={cn(
                  'w-4 h-4 text-tenant-primary-action-color cursor-pointer accent-tenant-primary-action-color',
                  'focus:ring-tenant-primary-action-color focus:ring-2',
                  radioClassName,
                )}
              />
              <span>
                <span className="block text-sm md:text-base text-dark-blue">{option.label}</span>
                {option.description && (
                  <span className="mt-1 block text-sm text-description-text">
                    {option.description}
                  </span>
                )}
              </span>
            </label>
          )
        })}
      </div>

      {/* Helper text or error message */}
      <div className="h-5 mt-0.5">
        {shouldShowError || helperText ? (
          <p
            className={cn('text-xs min-h-4', hasError ? 'text-red-600' : 'text-placeholder')}
            role={hasError ? 'alert' : undefined}
          >
            {errorText || error || helperText}
          </p>
        ) : (
          <div className="h-5"></div>
        )}
      </div>
    </div>
  )
}
